// A delivered output's file name for a download (G1 decision 19), for every page that saves one.
import type { GenerationOutput, Job } from "../../api/generated/model";

const EXTENSION: Readonly<Record<string, string>> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/quicktime": "mov",
  "audio/mpeg": "mp3",
  "audio/wav": "wav",
  "audio/x-wav": "wav",
  "audio/ogg": "ogg",
  "audio/mp4": "m4a",
  "audio/webm": "webm",
};

/**
 * Live's `normalizeFileName` (`common/utilites/upload-files.ts`), so a name is safe on every
 * system: anything outside `[a-zA-Z0-9._-]` becomes "-", a run of "-" one, and a leading "-" or
 * "." goes. The server names a saved file by the same rule.
 */
export function normalizeFileName(raw: string): string {
  return raw
    .replace(/[^a-zA-Z0-9._-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^[-.]+/, "");
}

/** A file's extension from its MIME type: the usual one, else the subtype, else "bin". */
export function extensionOf(mime: string | null): string {
  return EXTENSION[mime ?? ""] ?? mime?.split("/")[1] ?? "bin";
}

/**
 * A delivered output's file name (G1 decision 19): the prompt's first words as a slug, the
 * output's number and its type's extension, as "a-red-boot-on-wet-rocks-1.png"; without a kept
 * prompt, "created-ai-<job's first 8 characters>-1.png". A talking head's row numbers its videos
 * across its jobs (`number`), since jobs started together share their ids' first characters.
 */
export function outputFileName(
  job: Job,
  output: GenerationOutput,
  number = output.index + 1,
): string {
  const slug = (job.params?.prompt ?? "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/, "");
  const base = slug === "" ? `created-ai-${job.id.slice(0, 8)}` : slug;
  return `${base}-${number}.${extensionOf(output.mime)}`;
}
